import { t } from "i18next";
import React, { useState, useEffect, useRef } from "react";
import { analytic } from "../utils/Analytics";

const Header = ({ bot, handleRefresh, primary, handleToggleWidth, view, isIntialLanguageSelected, handleOpenVoiceMode, showVoiceMode, publicMode }) => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  
  const headerRef = useRef(null);
  const language = localStorage.getItem('language') || 'en';

  const formatText = (text) => {
    if (!text) return '';
    // Replace double line breaks with proper spacing
    return text.split('\n\n').map((paragraph, index) => (
      <React.Fragment key={index}>
        {paragraph.split('\n').map((line, lineIndex) => (
          <span key={lineIndex} className="block">
            {line}
          </span>
        ))}
        {index < text.split('\n\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  useEffect(() => {
    function updateHeaderHeight() {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    }

    updateHeaderHeight();

    window.addEventListener('resize', updateHeaderHeight);

    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  useEffect(() => {
    if (showInfoModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showInfoModal]);

  const handleVoiceModeClick = () => {
    handleOpenVoiceMode();
    setShowInfoModal(false);
  }

  const handleShowInfoModal = () => {
    setShowInfoModal((prev) => !prev);
    
    if(!showInfoModal) analytic("view_description", null);
  }

  return (
    <>
      <div ref={headerRef} className={`flex items-center justify-between pt-[8px] py-3 sm:py-3 gap-2 border-b-2 px-3 border-gray-200 relative`} style={{ backgroundColor: primary, zIndex: 10 }}>
        <div className="relative flex items-center space-x-4">
          <img src={bot.image} alt="Brainstormer Logo" className="w-10 sm:w-16 h-10 sm:h-16 rounded-full" />
          <div className="flex flex-col leading-tight">
            <div className="text-lg sm:text-xl leading-6 flex items-center">
              <span className="text-white mr-3">{bot.name}</span>
              {/* Info icon beside name - only show if description is available */}
              {bot.description && (
                <button
                  className="ml-1 p-1 rounded-[10px] bg-white/20 outline-none border-none w-[32px] h-[32px] leading-[10px]"
                  onClick={() => handleShowInfoModal()}
                  aria-label="Show info"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" />
                    <rect x="11" y="10" width="2" height="7" rx="1" fill="white" />
                    <rect x="11" y="7" width="2" height="2" rx="1" fill="white" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3">
          {showVoiceMode && <button className="bg-white rounded-md py-1.5 px-2.5 md:px-3 md:py-2 flex items-center justify-center gap-1.5 border-0 outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none" onClick={handleVoiceModeClick} disabled={!isIntialLanguageSelected}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.6088 5.18453L13.4299 5.59615C13.4019 5.66317 13.3548 5.72043 13.2943 5.7607C13.2339 5.80097 13.1629 5.82246 13.0903 5.82246C13.0176 5.82246 12.9466 5.80097 12.8862 5.7607C12.8258 5.72043 12.7786 5.66317 12.7506 5.59615L12.5717 5.18453C12.2572 4.45637 11.6812 3.87254 10.9573 3.54825L10.4053 3.30171C10.3383 3.27091 10.2816 3.22155 10.2418 3.15949C10.2021 3.09742 10.1809 3.02526 10.1809 2.95155C10.1809 2.87784 10.2021 2.80568 10.2418 2.74361C10.2816 2.68155 10.3383 2.63219 10.4053 2.60139L10.9267 2.3694C11.6688 2.03587 12.2547 1.43045 12.5637 0.677844L12.7485 0.233502C12.7755 0.164672 12.8226 0.10558 12.8837 0.0639286C12.9448 0.0222776 13.017 0 13.091 0C13.1649 0 13.2372 0.0222776 13.2983 0.0639286C13.3594 0.10558 13.4065 0.164672 13.4335 0.233502L13.6175 0.677117C13.9262 1.42986 14.5119 2.03555 15.2538 2.3694L15.776 2.60211C15.8427 2.63301 15.8992 2.68236 15.9389 2.74434C15.9785 2.80633 15.9995 2.87835 15.9995 2.95191C15.9995 3.02548 15.9785 3.0975 15.9389 3.15949C15.8992 3.22147 15.8427 3.27082 15.776 3.30171L15.2233 3.54752C14.4995 3.87213 13.9237 4.45622 13.6095 5.18453M4.72704 5.09145C4.72704 4.6898 4.40145 4.36421 3.9998 4.36421V4.36421C3.59816 4.36421 3.27257 4.6898 3.27257 5.09145V12.3638C3.27257 12.7655 3.59816 13.0911 3.9998 13.0911V13.0911C4.40145 13.0911 4.72704 12.7655 4.72704 12.3638V5.09145ZM1.45447 8.00039C1.45447 7.59875 1.12888 7.27316 0.727237 7.27316V7.27316C0.325595 7.27316 0 7.59875 0 8.00039V9.45487C0 9.85651 0.325595 10.1821 0.727237 10.1821V10.1821C1.12888 10.1821 1.45447 9.85651 1.45447 9.45487V8.00039ZM7.99961 2.1825C7.99961 1.78086 7.67401 1.45526 7.27237 1.45526V1.45526C6.87073 1.45526 6.54513 1.78086 6.54513 2.1825V15.2728C6.54513 15.6744 6.87073 16 7.27237 16V16C7.67401 16 7.99961 15.6744 7.99961 15.2728V2.1825ZM11.2722 6.54592C11.2722 6.14428 10.9466 5.81868 10.5449 5.81868V5.81868C10.1433 5.81868 9.8177 6.14428 9.8177 6.54592V12.3638C9.8177 12.7655 10.1433 13.0911 10.5449 13.0911V13.0911C10.9466 13.0911 11.2722 12.7655 11.2722 12.3638V6.54592ZM14.5447 8.00039C14.5447 7.59875 14.2191 7.27316 13.8175 7.27316V7.27316C13.4159 7.27316 13.0903 7.59875 13.0903 8.00039V9.45487C13.0903 9.85651 13.4159 10.1821 13.8175 10.1821V10.1821C14.2191 10.1821 14.5447 9.85651 14.5447 9.45487V8.00039Z" fill={primary} />
            </svg>
            <p className="text-sm m-0 font-semibold w-max" style={{ color: primary }}>{t('Voice Chat')}</p>
          </button>}
          {!publicMode && view && <div className="cursor-pointer flex" onClick={handleToggleWidth}>
            <img src={view === 'wide' ? "/assets/images/narow-icon.svg" : "/assets/images/wide-icon.svg"} width="20" height="20" alt="Refresh Icon" />
          </div>}
        </div>
      </div>
      
      {/* Full Screen Expandable Content */}
      <div
        className={`fixed left-0 w-full z-[9] overflow-hidden transition-all duration-500 ease-in-out ${
          showInfoModal 
            ? 'max-h-screen' 
            : 'max-h-0'
        }`}
        style={{
          backgroundColor: primary,
          top: headerHeight,
          height: `calc(100vh - ${headerHeight}px)`,
        }}
      >
        <div className="flex flex-col text-white h-full">
          <div className="flex-1 overflow-y-auto px-6 pb-48 md:pb-40 pt-8">
            <div className="text-base sm:text-md whitespace-pre-line">
              {formatText(bot.description)}
            </div>
          </div>
          <div className="flex justify-center bg-transparent pb-8">
            <button
              className="bg-white font-semibold py-2 px-4 shadow-lg text-lg hover:bg-gray-100 transition-all rounded-xl border-[0.68px] border-stone-50 backdrop-blur-lg flex items-center gap-2"
              style={{ color: primary, border: `1px solid ${primary}`, backgroundColor: `${primary}` }}
              onClick={() => setShowInfoModal(false)}
            >
              <svg width="23" height="20" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M22.2222 19.3534C19.5037 16.0349 17.0896 14.152 14.98 13.7046C12.8704 13.2571 10.8619 13.1896 8.95444 13.5018V19.4446L0 9.74733L8.95444 0.555664V6.204C12.4815 6.23178 15.48 7.49715 17.95 10.0001C20.4196 12.5031 21.8437 15.6208 22.2222 19.3534Z" fill="currentColor" />
              </svg>
              Continue Chat
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default Header;